import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Database, RefreshCw, Search, ExternalLink, CheckCircle2,
  FlaskConical, Star, Settings2, Activity,
} from "lucide-react";
import { api, formatCOP } from "../lib/apiClient";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import SecopLiveSearchPanel from "../components/SecopLiveSearchPanel";

const ESTADO_BADGE = {
  analizado: "bg-emerald-50 border-emerald-200 text-emerald-800",
  en_analisis: "bg-amber-50 border-amber-200 text-amber-800",
};
const ESTADO_LABEL = { analizado: "Analizado", en_analisis: "En análisis" };

export default function AdminSecopContractsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], count: 0, facets: {}, totals: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [dataset, setDataset] = useState("");
  const [modalidad, setModalidad] = useState("");
  const [estado, setEstado] = useState("");
  const [verified, setVerified] = useState("");
  const [reloadTick, setReloadTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "200" });
    if (q) params.set("q", q);
    if (dataset) params.set("dataset", dataset);
    if (modalidad) params.set("modalidad", modalidad);
    if (estado) params.set("estado", estado);
    if (verified) params.set("verified", verified);
    try {
      const { data: res } = await api.get(`/admin/secop/contracts?${params.toString()}`);
      setData(res);
    } catch (_) {
      /* silencio: el endpoint puede fallar puntualmente, la UI muestra vacíos */
    } finally {
      setLoading(false);
    }
  }, [q, dataset, modalidad, estado, verified]);

  useEffect(() => { load(); }, [load, reloadTick]);

  const refresh = useCallback(() => setReloadTick((t) => t + 1), []);
  const submitSearch = useCallback((e) => { e.preventDefault(); setQ(qInput.trim()); }, [qInput]);

  const totals = data.totals || {};

  return (
    <div data-testid="admin-secop-contracts-page" className="space-y-6">
      <header className="flex items-center justify-between border-b border-gray-200 pb-3">
        <div>
          <h1 className="font-serif text-2xl text-gray-900">Contratos SECOP</h1>
          <p className="text-xs text-gray-500 mt-1">
            Base unificada de contratos usados como comparables en prevalidación, diagnóstico y kit documental.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/secop/health">
            <Button variant="outline" className="rounded-sm h-9 text-xs" data-testid="secop-go-health">
              <Activity size={14} className="mr-1.5" /> Dataset Health
            </Button>
          </Link>
          <Link to="/admin/secop/config">
            <Button variant="outline" className="rounded-sm h-9 text-xs" data-testid="secop-go-config">
              <Settings2 size={14} className="mr-1.5" /> Configuración
            </Button>
          </Link>
          <Button variant="outline" onClick={refresh} disabled={loading} className="rounded-sm h-9 text-xs" data-testid="secop-contracts-refresh">
            <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </Button>
        </div>
      </header>

      {/* Búsqueda en vivo en SECOP (panel colapsable) */}
      <SecopLiveSearchPanel />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard label="Total contratos" value={totals.total ?? 0} icon={<Database size={16} className="text-indigo-600" />} testid="kpi-secop-total" />
        <KpiCard label="Analizados" value={totals.analizados ?? 0} icon={<CheckCircle2 size={16} className="text-emerald-600" />} testid="kpi-secop-analizados" />
        <KpiCard label="En análisis" value={totals.en_analisis ?? 0} icon={<FlaskConical size={16} className="text-amber-600" />} testid="kpi-secop-en-analisis" />
        <KpiCard label="Verificados" value={totals.verificados ?? 0} icon={<Star size={16} className="text-sky-600" />} testid="kpi-secop-verificados" />
      </div>

      {/* Filtros */}
      <div className="border border-gray-200 bg-white p-4" data-testid="secop-contracts-filters">
        <form onSubmit={submitSearch} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Buscar</label>
            <div className="flex gap-2">
              <Input
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                placeholder="Objeto, entidad, adjudicatario, ID SECOP…"
                className="h-9 text-xs rounded-sm"
                data-testid="secop-search-input"
              />
              <Button type="submit" variant="outline" className="rounded-sm h-9 px-3" data-testid="secop-search-btn">
                <Search size={14} />
              </Button>
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Estado</label>
            <select value={estado} onChange={(e) => setEstado(e.target.value)} className="w-full h-9 px-2 text-xs border border-gray-300 rounded-sm bg-white" data-testid="secop-filter-estado">
              <option value="">Todos</option>
              <option value="en_analisis">En análisis</option>
              <option value="analizado">Analizado</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Dataset</label>
            <select value={dataset} onChange={(e) => setDataset(e.target.value)} className="w-full h-9 px-2 text-xs border border-gray-300 rounded-sm bg-white" data-testid="secop-filter-dataset">
              <option value="">Todos</option>
              {(data.facets?.datasets || []).map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Verificado</label>
            <select value={verified} onChange={(e) => setVerified(e.target.value)} className="w-full h-9 px-2 text-xs border border-gray-300 rounded-sm bg-white" data-testid="secop-filter-verified">
              <option value="">Todos</option>
              <option value="true">Sí</option>
              <option value="false">No</option>
            </select>
          </div>
        </form>
        {(data.facets?.modalidades || []).length > 0 && (
          <div className="mt-3">
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Modalidad</label>
            <select value={modalidad} onChange={(e) => setModalidad(e.target.value)} className="w-full md:w-1/3 h-9 px-2 text-xs border border-gray-300 rounded-sm bg-white" data-testid="secop-filter-modalidad">
              <option value="">Todas</option>
              {data.facets.modalidades.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Tabla */}
      <div className="border border-gray-200 bg-white overflow-hidden" data-testid="secop-contracts-table-wrap">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-serif text-base">Contratos ({data.count})</h2>
          {loading && <span className="text-xs text-gray-500">Cargando…</span>}
        </div>
        {data.items.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500" data-testid="secop-contracts-empty">
            No hay contratos SECOP en la base con estos filtros. Los contratos se agregan al seleccionar
            comparables SECOP dentro de un caso.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="secop-contracts-table">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left">Objeto / Entidad</th>
                  <th className="px-3 py-2 text-left">Estado</th>
                  <th className="px-3 py-2 text-left">Dataset</th>
                  <th className="px-3 py-2 text-right">Valor</th>
                  <th className="px-3 py-2 text-left">Modalidad</th>
                  <th className="px-3 py-2 text-center">Casos</th>
                  <th className="px-3 py-2 text-center">Feedback</th>
                  <th className="px-3 py-2 text-left">Último uso</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it, i) => (
                  <tr
                    key={it.contract_key}
                    onClick={() => navigate(`/admin/secop/contracts/${encodeURIComponent(it.contract_key)}`)}
                    className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                    data-testid={`secop-contract-row-${i}`}
                  >
                    <td className="px-3 py-2 max-w-md">
                      <Link
                        to={`/admin/secop/contracts/${encodeURIComponent(it.contract_key)}`}
                        className="block hover:underline text-gray-900 font-medium truncate"
                        title={it.objeto}
                        data-testid={`secop-contract-link-${i}`}
                      >
                        {it.objeto || "—"}
                      </Link>
                      <div className="text-gray-500 truncate">{it.entidad || "—"}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 border rounded-sm text-[10px] whitespace-nowrap ${ESTADO_BADGE[it.estado] || ""}`}>
                        {ESTADO_LABEL[it.estado] || it.estado}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {it.dataset}
                      {it.verified && <CheckCircle2 size={11} className="inline ml-1 text-emerald-600" title="Verificado (dominio oficial)" />}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap">
                      {it.valor_cop != null ? formatCOP(it.valor_cop) : "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-600 max-w-[160px] truncate" title={it.modalidad}>{it.modalidad || "—"}</td>
                    <td className="px-3 py-2 text-center text-gray-700">{it.cases_count}</td>
                    <td className="px-3 py-2 text-center text-gray-700">
                      {it.feedback_count > 0 ? (
                        <span title={`${it.useful_count} útil(es)${it.avg_rating ? ` · rating ${it.avg_rating}` : ""}`}>
                          {it.useful_count}/{it.feedback_count}
                          {it.avg_rating != null && (
                            <span className="ml-1 text-amber-600">★{it.avg_rating}</span>
                          )}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                      {it.last_used_at ? new Date(it.last_used_at).toLocaleDateString("es-CO") : "—"}
                      {it.source_url && (
                        <a href={it.source_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="ml-2 text-gray-400 hover:text-primary inline-block align-middle" title="Abrir en SECOP" data-testid={`secop-source-link-${i}`}>
                          <ExternalLink size={11} />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon, testid }) {
  return (
    <div className="border border-gray-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-gray-500 mb-1">
        <span>{label}</span>
        {icon}
      </div>
      <div className="font-serif text-2xl text-gray-900">{value}</div>
    </div>
  );
}
